import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { http, apiError } from "@/lib/api";
import { ArrowLeft, ForkKnife, Plus, Trash, FloppyDisk, Copy, Sparkle, ShoppingCart, CheckSquare, Square, ArrowsClockwise } from "@phosphor-icons/react";
import ClientNutritionBrief from "@/components/ClientNutritionBrief";

const DEFAULT_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((n) => ({
  name: n, meals: [{ slot: "Breakfast", title: "", notes: "" }, { slot: "Lunch", title: "", notes: "" }, { slot: "Dinner", title: "", notes: "" }],
}));
const SLOT_OPTIONS = ["Breakfast", "Snack 1", "Lunch", "Snack 2", "Dinner", "Pre-workout", "Post-workout"];
const CATEGORIES = ["Produce", "Protein", "Pantry", "Dairy", "Frozen", "Other"];

export default function CoachClientMeals() {
  const { clientId } = useParams();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [plan, setPlan] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeDay, setActiveDay] = useState(0);
  const [view, setView] = useState("meals"); // "meals" | "shopping"
  const [estimatingIdx, setEstimatingIdx] = useState(null); // which meal is auto-filling
  const [genLoading, setGenLoading] = useState(false); // shopping list generation
  const [slSaving, setSlSaving] = useState(false);
  // Deep-clone meals from src day to dst day. Copies name unchanged on dst.
  const cloneDayMeals = (src) => ({
    meals: (src.meals || []).map((m) => ({
      slot: m.slot, title: m.title || "", notes: m.notes || "",
      protein_g: m.protein_g ?? null, calories: m.calories ?? null,
    })),
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: m }] = await Promise.all([
        http.get(`/coach/clients/${clientId}`),
        http.get(`/coach/clients/${clientId}/meal-plan`),
      ]);
      setClient(c?.client || c);
      setPlan(m?.plan || null);
      setTemplates(m?.templates || []);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [clientId]);

  const ensurePlan = () => {
    if (plan) return plan;
    const draft = {
      name: "Custom Meal Plan",
      description: "",
      days: DEFAULT_DAYS,
      coach_notes: "",
      daily_calorie_target: null,
      daily_protein_target: null,
    };
    setPlan(draft);
    return draft;
  };

  const cloneTemplate = async (templateId) => {
    setSaving(true); setErr(""); setMsg("");
    try {
      const { data } = await http.put(`/coach/clients/${clientId}/meal-plan`, {
        name: templates.find((t) => t.id === templateId)?.name || "Custom Meal Plan",
        template_meal_program_id: templateId,
      });
      setPlan(data);
      setMsg("Template cloned. Customise the days below.");
    } catch (e) { setErr(apiError(e)); }
    finally { setSaving(false); }
  };

  const setDay = (idx, updater) => {
    setPlan((cur) => {
      const base = cur || ensurePlan();
      const days = [...(base.days || [])];
      days[idx] = updater(days[idx]);
      return { ...base, days };
    });
  };
  const setMeal = (dayIdx, mealIdx, updater) => {
    setDay(dayIdx, (d) => {
      const meals = [...(d.meals || [])];
      meals[mealIdx] = updater(meals[mealIdx]);
      return { ...d, meals };
    });
  };
  const addMeal = (dayIdx) => setDay(dayIdx, (d) => ({ ...d, meals: [...(d.meals || []), { slot: "Snack 1", title: "", notes: "" }] }));
  const removeMeal = (dayIdx, mealIdx) => setDay(dayIdx, (d) => ({ ...d, meals: d.meals.filter((_, i) => i !== mealIdx) }));

  // FEATURE 1a: Copy meals from another day into the active day
  const copyDayFrom = (srcIdx) => {
    if (!plan || srcIdx === activeDay) return;
    const src = plan.days?.[srcIdx];
    if (!src) return;
    setPlan((cur) => {
      const days = [...cur.days];
      days[activeDay] = { ...days[activeDay], ...cloneDayMeals(src) };
      return { ...cur, days };
    });
    setMsg(`Copied meals from ${src.name} → ${plan.days[activeDay]?.name}. Don't forget to save.`);
  };

  // FEATURE 1b: Copy Monday to weekdays (Tue-Fri) — assumes standard 7-day layout
  const copyMonToWeekdays = () => {
    if (!plan?.days || plan.days.length < 5) return;
    const src = plan.days[0]; // MON is index 0 in default layout
    setPlan((cur) => {
      const days = [...cur.days];
      for (let i = 1; i <= 4 && i < days.length; i++) {
        days[i] = { ...days[i], ...cloneDayMeals(src) };
      }
      return { ...cur, days };
    });
    setMsg(`Copied ${src.name}'s meals to Tue-Fri. Don't forget to save.`);
  };

  // FEATURE 2: AI auto-fill calories & protein for a single meal
  const estimateMeal = async (mealIdx) => {
    const m = plan?.days?.[activeDay]?.meals?.[mealIdx];
    if (!m || !(m.title || "").trim()) {
      setErr("Add a meal description first, then auto-fill.");
      return;
    }
    setErr(""); setEstimatingIdx(mealIdx);
    try {
      const { data } = await http.post("/coach/meal-plans/estimate-nutrition", {
        title: m.title, notes: m.notes || null,
      });
      setMeal(activeDay, mealIdx, (x) => ({ ...x, calories: data.calories, protein_g: data.protein_g }));
      setMsg(`Auto-filled ${data.calories} kcal · ${data.protein_g}g protein for "${m.title.slice(0, 30)}${m.title.length > 30 ? "..." : ""}"`);
    } catch (e) {
      setErr(apiError(e, "Auto-fill failed."));
    } finally { setEstimatingIdx(null); }
  };

  // FEATURE 3a: Generate shopping list from the entire week's meals via AI
  const generateShoppingList = async () => {
    setErr(""); setMsg(""); setGenLoading(true);
    try {
      const { data } = await http.post(`/coach/clients/${clientId}/meal-plan/generate-shopping-list`);
      setPlan((cur) => ({ ...cur, shopping_list: data }));
      setMsg(`Shopping list generated · ${(data.items || []).length} items across ${new Set((data.items || []).map((i) => i.category)).size} categories.`);
    } catch (e) {
      setErr(apiError(e, "Generation failed. Add meals first, then generate."));
    } finally { setGenLoading(false); }
  };

  // FEATURE 3b: Manually save/edit shopping list items
  const saveShoppingList = async () => {
    if (!plan?.shopping_list) return;
    // Drop blank rows so the Pydantic min_length=1 on `name` doesn't 422 the
    // whole save when the coach forgets to fill in a manually-added row.
    const cleanItems = (plan.shopping_list.items || []).filter((i) => (i.name || "").trim());
    setSlSaving(true); setErr(""); setMsg("");
    try {
      const { data } = await http.patch(`/coach/clients/${clientId}/meal-plan/shopping-list`, {
        items: cleanItems,
      });
      setPlan((cur) => ({ ...cur, shopping_list: data }));
      const dropped = (plan.shopping_list.items || []).length - cleanItems.length;
      setMsg(`Shopping list saved · Client will see this on their meals page.${dropped > 0 ? ` (Skipped ${dropped} blank ${dropped === 1 ? "row" : "rows"}.)` : ""}`);
    } catch (e) { setErr(apiError(e, "Save failed.")); }
    finally { setSlSaving(false); }
  };

  const updateSlItem = (idx, updater) => {
    setPlan((cur) => {
      const items = [...((cur.shopping_list?.items) || [])];
      items[idx] = updater(items[idx]);
      return { ...cur, shopping_list: { ...cur.shopping_list, items } };
    });
  };
  const removeSlItem = (idx) => setPlan((cur) => {
    const items = ((cur.shopping_list?.items) || []).filter((_, i) => i !== idx);
    return { ...cur, shopping_list: { ...cur.shopping_list, items } };
  });
  const addSlItem = () => setPlan((cur) => {
    const items = [...((cur.shopping_list?.items) || []), { category: "Other", name: "", quantity: "", checked: false }];
    return { ...cur, shopping_list: { ...(cur.shopping_list || {}), items } };
  });

  const savePlan = async () => {
    if (!plan) return;
    setSaving(true); setErr(""); setMsg("");
    try {
      const { data } = await http.put(`/coach/clients/${clientId}/meal-plan`, {
        name: plan.name || "Custom Meal Plan",
        description: plan.description || "",
        days: plan.days || [],
        coach_notes: plan.coach_notes || "",
        daily_calorie_target: plan.daily_calorie_target ? Number(plan.daily_calorie_target) : null,
        daily_protein_target: plan.daily_protein_target ? Number(plan.daily_protein_target) : null,
      });
      setPlan(data);
      setMsg("Saved · Client will see this on their /app/meals page.");
    } catch (e) { setErr(apiError(e)); }
    finally { setSaving(false); }
  };

  const day = plan?.days?.[activeDay];

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="coach-client-meals-page">
      <button onClick={() => navigate(`/coach/clients/${clientId}`)} className="text-zinc-400 hover:text-white text-xs uppercase tracking-[0.15em] flex items-center gap-1 mb-6" data-testid="meals-back">
        <ArrowLeft size={14} weight="bold" /> Back to client
      </button>

      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-[#FF3333] flex items-center justify-center">
          <ForkKnife size={20} weight="bold" color="#fff" />
        </div>
        <div>
          <div className="id-label">Meal plan</div>
          <h1 className="font-oswald text-3xl font-bold uppercase tracking-wide leading-tight">{client?.name || "Client"}</h1>
        </div>
      </div>

      {loading ? (
        <div className="id-label text-zinc-500 mt-10 id-pulse">— Loading —</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
          {/* LEFT — Brief */}
          <div className="lg:col-span-4 xl:col-span-3">
            <ClientNutritionBrief clientId={clientId} />
          </div>

          {/* RIGHT — Editor */}
          <div className="lg:col-span-8 xl:col-span-9 space-y-4">
            {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2 text-xs text-[#FF3333]">{err}</div>}
            {msg && <div className="border border-emerald-700 bg-emerald-700/10 px-3 py-2 text-xs text-emerald-400">{msg}</div>}

            {/* Templates */}
            {!plan && templates.length > 0 && (
              <div className="id-card">
                <div className="id-label mb-3">Start from a template</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {templates.map((t) => (
                    <button key={t.id} onClick={() => cloneTemplate(t.id)} className="text-left border border-[#262626] hover:border-[#FF3333] bg-[#0F0F0F] px-4 py-3 transition-colors" data-testid={`tpl-${t.id}`}>
                      <div className="font-oswald uppercase tracking-wide text-sm">{t.name}</div>
                      <div className="text-xs text-zinc-500 mt-0.5 font-manrope">{t.description || `${t.days?.length || 0} days`}</div>
                    </button>
                  ))}
                </div>
                <div className="mt-3 text-xs text-zinc-500 font-manrope">
                  Or skip — <button onClick={ensurePlan} className="text-[#FF3333] underline" data-testid="start-blank">start blank</button>.
                </div>
              </div>
            )}

            {!plan && templates.length === 0 && (
              <div className="id-card">
                <div className="id-label mb-2">No meal plan yet</div>
                <p className="text-zinc-400 text-sm mb-4 font-manrope">No library templates either. Start blank — you can build the week from scratch.</p>
                <button onClick={ensurePlan} className="id-btn-primary" data-testid="start-blank-empty">Start a blank plan</button>
              </div>
            )}

            {plan && (
              <>
                {/* Plan header */}
                <div className="id-card">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-5">
                      <label className="id-label block mb-1">Plan name</label>
                      <input value={plan.name || ""} onChange={(e) => setPlan({ ...plan, name: e.target.value })} className="id-input" data-testid="plan-name" />
                    </div>
                    <div className="md:col-span-3">
                      <label className="id-label block mb-1">Daily calories</label>
                      <input type="number" value={plan.daily_calorie_target || ""} onChange={(e) => setPlan({ ...plan, daily_calorie_target: e.target.value })} className="id-input" placeholder="e.g. 2200" data-testid="plan-cal" />
                    </div>
                    <div className="md:col-span-3">
                      <label className="id-label block mb-1">Daily protein (g)</label>
                      <input type="number" value={plan.daily_protein_target || ""} onChange={(e) => setPlan({ ...plan, daily_protein_target: e.target.value })} className="id-input" placeholder="e.g. 180" data-testid="plan-protein" />
                    </div>
                    <div className="md:col-span-1 flex items-end">
                      <button onClick={savePlan} disabled={saving} className="id-btn-primary w-full" data-testid="plan-save">
                        <FloppyDisk size={14} weight="bold" /> {saving ? "..." : "Save"}
                      </button>
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="id-label block mb-1">Coach notes (shown to client)</label>
                    <textarea value={plan.coach_notes || ""} onChange={(e) => setPlan({ ...plan, coach_notes: e.target.value })} rows={2} className="id-input" placeholder="e.g. Swap any breakfast for the smoothie if pressed for time. Aim for 2g protein per kg." data-testid="plan-notes" />
                  </div>
                </div>

                {/* View toggle: Meals vs Shopping List */}
                <div className="flex gap-1 border-b border-[#262626]" data-testid="meal-view-toggle">
                  <button
                    onClick={() => setView("meals")}
                    data-testid="view-meals-tab"
                    className={`px-5 py-3 uppercase tracking-[0.15em] text-xs font-bold border-b-2 transition-colors ${view === "meals" ? "border-[#FF3333] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
                  >
                    <ForkKnife size={13} weight="bold" className="inline mr-2" /> Meals
                  </button>
                  <button
                    onClick={() => setView("shopping")}
                    data-testid="view-shopping-tab"
                    className={`px-5 py-3 uppercase tracking-[0.15em] text-xs font-bold border-b-2 transition-colors ${view === "shopping" ? "border-[#FF3333] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
                  >
                    <ShoppingCart size={13} weight="bold" className="inline mr-2" /> Shopping List
                    {plan.shopping_list?.items?.length ? (
                      <span className="ml-2 text-[10px] bg-[#FF3333]/20 text-[#FF3333] px-1.5 py-0.5">{plan.shopping_list.items.length}</span>
                    ) : null}
                  </button>
                </div>

                {view === "meals" && <>

                {/* Day tabs + bulk copy */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex gap-1 overflow-x-auto" data-testid="plan-day-tabs">
                    {(plan.days || []).map((d, i) => (
                      <button key={i} onClick={() => setActiveDay(i)} data-testid={`plan-day-tab-${i}`}
                        className={`flex-shrink-0 px-4 py-2 border uppercase tracking-[0.15em] text-xs font-bold transition-colors ${activeDay === i ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#262626] text-zinc-400 hover:text-white"}`}>
                        {d.name}
                      </button>
                    ))}
                  </div>
                  {(plan.days || []).length >= 5 && (
                    <button
                      onClick={copyMonToWeekdays}
                      className="id-btn-ghost text-xs shrink-0 border-[#FF3333]/40 text-[#FF3333] hover:bg-[#FF3333] hover:text-white"
                      data-testid="copy-mon-to-weekdays"
                      title="Copy Monday's meals to Tuesday-Friday"
                    >
                      <Copy size={12} weight="bold" /> Copy Mon → Tue-Fri
                    </button>
                  )}
                </div>

                {/* Day editor */}
                {day && (
                  <div className="id-card" data-testid="plan-day-card">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                      <input value={day.name} onChange={(e) => setDay(activeDay, (d) => ({ ...d, name: e.target.value }))} className="id-input max-w-[160px] font-oswald uppercase tracking-wide text-lg" data-testid="day-name" />
                      <div className="flex items-center gap-2">
                        {/* FEATURE 1a: Copy from another day */}
                        <select
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v !== "") copyDayFrom(Number(v));
                            e.target.value = ""; // reset selection so it always fires
                          }}
                          defaultValue=""
                          className="id-input text-xs !w-auto uppercase tracking-[0.1em]"
                          data-testid="copy-day-from"
                          title="Copy meals from another day into this one"
                        >
                          <option value="" disabled>{"⤴ Copy from…"}</option>
                          {(plan.days || []).map((d, i) => i !== activeDay ? (
                            <option key={i} value={i}>{d.name}</option>
                          ) : null)}
                        </select>
                        <button onClick={() => addMeal(activeDay)} className="id-btn-secondary text-xs" data-testid="add-meal">
                          <Plus size={12} weight="bold" /> Add meal
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {(day.meals || []).map((m, mi) => (
                        <div key={mi} className="border border-[#262626] bg-[#0F0F0F] p-3" data-testid={`meal-${mi}`}>
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                            <select
                              value={m.slot}
                              onChange={(e) => setMeal(activeDay, mi, (x) => ({ ...x, slot: e.target.value }))}
                              className="id-input md:col-span-2 uppercase text-xs tracking-[0.1em]"
                              data-testid={`meal-${mi}-slot`}
                            >
                              {SLOT_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <input value={m.title} onChange={(e) => setMeal(activeDay, mi, (x) => ({ ...x, title: e.target.value }))} className="id-input md:col-span-4" placeholder="e.g. 3 eggs scrambled + 2 slices wholegrain toast + 1 banana" data-testid={`meal-${mi}-title`} />
                            <input type="number" value={m.calories || ""} onChange={(e) => setMeal(activeDay, mi, (x) => ({ ...x, calories: e.target.value ? Number(e.target.value) : null }))} className="id-input md:col-span-2" placeholder="Calories" data-testid={`meal-${mi}-cal`} />
                            <input type="number" value={m.protein_g || ""} onChange={(e) => setMeal(activeDay, mi, (x) => ({ ...x, protein_g: e.target.value ? Number(e.target.value) : null }))} className="id-input md:col-span-2" placeholder="Protein (g)" data-testid={`meal-${mi}-protein`} />
                            {/* FEATURE 2: AI auto-fill */}
                            <button
                              onClick={() => estimateMeal(mi)}
                              disabled={estimatingIdx === mi || !(m.title || "").trim()}
                              className="md:col-span-1 border border-[#FF3333]/40 hover:border-[#FF3333] hover:bg-[#FF3333]/10 text-[#FF3333] disabled:opacity-30 disabled:cursor-not-allowed text-xs px-2 py-2 flex items-center justify-center transition-colors"
                              data-testid={`meal-${mi}-autofill`}
                              title="Auto-fill Calories & Protein using AI"
                            >
                              {estimatingIdx === mi ? (
                                <ArrowsClockwise size={14} weight="bold" className="animate-spin" />
                              ) : (
                                <Sparkle size={14} weight="fill" />
                              )}
                            </button>
                            <button onClick={() => removeMeal(activeDay, mi)} className="md:col-span-1 border border-[#262626] hover:border-[#FF3333] hover:text-[#FF3333] text-zinc-500 text-xs px-2 py-2 flex items-center justify-center" data-testid={`meal-${mi}-delete`}>
                              <Trash size={14} weight="bold" />
                            </button>
                          </div>
                          <textarea value={m.notes || ""} onChange={(e) => setMeal(activeDay, mi, (x) => ({ ...x, notes: e.target.value }))} rows={2} className="id-input mt-2" placeholder="Ingredients, prep, swaps..." data-testid={`meal-${mi}-notes`} />
                        </div>
                      ))}
                      {(!day.meals || day.meals.length === 0) && (
                        <div className="text-center text-zinc-500 text-sm font-manrope py-6">
                          No meals yet. <button onClick={() => addMeal(activeDay)} className="text-[#FF3333] underline">Add the first meal</button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Final save */}
                <div className="flex justify-end">
                  <button onClick={savePlan} disabled={saving} className="id-btn-primary" data-testid="plan-save-bottom">
                    <FloppyDisk size={14} weight="bold" /> {saving ? "Saving..." : "Save meal plan"}
                  </button>
                </div>

                </>}

                {/* FEATURE 3: Shopping List view */}
                {view === "shopping" && (
                  <div className="id-card" data-testid="shopping-list-card">
                    <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                      <div>
                        <div className="id-label mb-1 flex items-center gap-2">
                          <ShoppingCart size={12} weight="fill" /> Shopping List
                        </div>
                        <p className="text-zinc-500 text-xs font-manrope">
                          {plan.shopping_list?.generated_at
                            ? `AI-generated · Coach-editable · Client sees this + can tick items off in their app.`
                            : `Not generated yet. Click "Generate from this week's meals" to let AI build it.`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={generateShoppingList}
                          disabled={genLoading}
                          className="id-btn-ghost text-xs border-[#FF3333]/40 text-[#FF3333] hover:bg-[#FF3333] hover:text-white disabled:opacity-40"
                          data-testid="generate-shopping-list-btn"
                        >
                          {genLoading ? (<><ArrowsClockwise size={12} weight="bold" className="animate-spin" /> Generating…</>) : (<><Sparkle size={12} weight="fill" /> {plan.shopping_list ? "Regenerate" : "Generate from this week"}</>)}
                        </button>
                        {plan.shopping_list?.items?.length > 0 && (
                          <button onClick={saveShoppingList} disabled={slSaving} className="id-btn-primary text-xs" data-testid="save-shopping-list-btn">
                            <FloppyDisk size={12} weight="bold" /> {slSaving ? "Saving…" : "Save"}
                          </button>
                        )}
                      </div>
                    </div>

                    {plan.shopping_list?.items?.length > 0 ? (
                      <div className="space-y-4" data-testid="shopping-list-items">
                        {CATEGORIES.map((cat) => {
                          const catItems = plan.shopping_list.items
                            .map((it, i) => ({ ...it, __i: i }))
                            .filter((it) => (it.category || "Other") === cat);
                          if (catItems.length === 0) return null;
                          return (
                            <div key={cat}>
                              <div className="id-label mb-2 text-[#FF3333]">— {cat} ({catItems.length}) —</div>
                              <div className="space-y-1.5">
                                {catItems.map((it) => (
                                  <div key={it.__i} className="grid grid-cols-12 gap-2 items-center border border-[#262626] bg-[#0F0F0F] p-2" data-testid={`sl-item-${it.__i}`}>
                                    <input
                                      value={it.name}
                                      onChange={(e) => updateSlItem(it.__i, (x) => ({ ...x, name: e.target.value }))}
                                      className="id-input col-span-6"
                                      placeholder="Item name"
                                      data-testid={`sl-item-${it.__i}-name`}
                                    />
                                    <input
                                      value={it.quantity}
                                      onChange={(e) => updateSlItem(it.__i, (x) => ({ ...x, quantity: e.target.value }))}
                                      className="id-input col-span-3"
                                      placeholder="Qty"
                                      data-testid={`sl-item-${it.__i}-qty`}
                                    />
                                    <select
                                      value={it.category || "Other"}
                                      onChange={(e) => updateSlItem(it.__i, (x) => ({ ...x, category: e.target.value }))}
                                      className="id-input col-span-2 text-xs uppercase tracking-[0.1em]"
                                      data-testid={`sl-item-${it.__i}-cat`}
                                    >
                                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                    <button
                                      onClick={() => removeSlItem(it.__i)}
                                      className="col-span-1 border border-[#262626] hover:border-[#FF3333] hover:text-[#FF3333] text-zinc-500 text-xs px-2 py-2 flex items-center justify-center"
                                      data-testid={`sl-item-${it.__i}-delete`}
                                    >
                                      <Trash size={14} weight="bold" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        <button onClick={addSlItem} className="id-btn-ghost text-xs" data-testid="sl-add-item">
                          <Plus size={12} weight="bold" /> Add item manually
                        </button>
                      </div>
                    ) : (
                      <div className="text-center py-10 text-zinc-500 text-sm font-manrope">
                        <ShoppingCart size={40} weight="thin" className="mx-auto mb-3 text-zinc-700" />
                        No shopping list yet.<br />
                        Click <span className="text-[#FF3333]">&quot;Generate from this week&quot;</span> to let AI build it from all meals.
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
